import { formatCurrency } from "@sellar-io/common";
import { Icon, H5 } from "@sellar-io/components";
import type { OrderLinkFlowGetResponseType } from "@sellar-io/api-v2/types";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  OrderSummaryItemList,
  type item,
} from "../../../../../components/newOrderSummary/OrderSummaryItemList";
import { OrderSummaryPrice } from "../../../../../components/newOrderSummary/OrderSummaryPrice";
import { zLayer } from "@sellar-io/components";

type OrderSummaryDrawerProps = {
  active: boolean;
  onClose: () => void;
  order: OrderLinkFlowGetResponseType;
};

export const OrderSummaryDrawer = ({
  active,
  onClose,
  order,
}: OrderSummaryDrawerProps) => {
  const placedDate = order.estimatedDeliveryDate
    ? new Date(order.estimatedDeliveryDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  return (
    <Transition show={active}>
      <Dialog onClose={onClose} className={`relative ${zLayer.drawer}`}>
        <TransitionChild>
          <DialogBackdrop className="fixed inset-0 bg-black/40 transition duration-300 ease-in-out data-closed:opacity-0" />
        </TransitionChild>
        <TransitionChild>
          <div className="fixed inset-0 flex w-screen items-center justify-end transition duration-300 ease-in-out data-closed:translate-x-full">
            <DialogPanel className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center gap-3 p-5 pb-0">
                <div className="shrink-0">
                  {order.supplierLogoUrl ? (
                    <img
                      src={order.supplierLogoUrl}
                      alt={order.supplierName}
                      className="h-10 w-10 rounded-lg object-contain"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                      <Icon icon="STOREFRONT_OUTLINE" size={1} />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-zinc-800">
                    {order.supplierName} - Order #{order.orderReference}
                  </p>
                  {placedDate && (
                    <p className="text-xs text-zinc-500">
                      Placed on {placedDate}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 text-zinc-400 hover:text-zinc-600"
                  aria-label="Close"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Details tab */}
              <div className="mt-4 border-b border-zinc-200 px-5">
                <div className="flex gap-6">
                  <span className="border-b-2 border-zinc-800 pb-2 text-sm font-semibold text-zinc-800">
                    Details
                  </span>
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-2 bg-zinc-100 p-4">
                <div className="rounded-2xl bg-white p-4 shadow-sm">
                  <H5 className="mb-4">Order Summary</H5>
                  <div className="-mx-4 mb-4 border-t border-zinc-200" />
                  <div className="flex flex-col gap-2">
                    <OrderSummaryItemList
                      items={order.lineItems.map((lineItem): item => ({
                        quantity: lineItem.quantity,
                        description: lineItem.productName,
                        cost: formatCurrency(lineItem.lineTotalPence),
                        promotionAppliedAt: null,
                      }))}
                      printPrice={true}
                    />
                    <OrderSummaryPrice
                      subTotal={formatCurrency(order.subtotalPence)}
                      delivery="FREE"
                      discount={
                        order.discountAmountPence !== 0
                          ? {
                              calculatedTotal: formatCurrency(
                                order.discountAmountPence,
                              ),
                              value: formatCurrency(order.discountAmountPence),
                            }
                          : undefined
                      }
                      tax={{
                        name: "VAT",
                        percentage: String(order.vatPercentage),
                        total: formatCurrency(order.vatAmountPence ?? 0),
                      }}
                      total={formatCurrency(order.totalPence)}
                      print={true}
                    />
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
